import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ChevronDown, ChevronUp } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { getTaskText, type Room, type Task } from "@/types/checklist";

export interface GuestReportData {
  property_name: string;
  property_city: string | null;
  report_created_at: string | null;
  cleaning_completed: boolean;
  checklist_completion_percent: number;
  all_tasks_completed: boolean;
  checklist_name: string;
  checklist_rooms: Room[];
  tasks_status: { room_name: string; task_text: string; completed: boolean }[];
  photos: { room_name: string; task_text: string; photo_url: string; type: string }[];
  proof_photos: string[];
  issues: { room_name: string; description: string; photo_url: string | null; severity: string | null }[];
}

async function fetchGuestReport(token: string): Promise<GuestReportData | null> {
  const trimmed = String(token ?? "").trim();
  if (!trimmed) return null;
  const { data, error } = await supabase.rpc("get_guest_report_by_token", { p_token: trimmed });
  if (error) {
    console.warn("[GuestReport] RPC error:", error.message, error.code, error.details);
    throw error;
  }
  return data as GuestReportData | null;
}

function placeLine(name: string, city: string | null): string {
  const n = name.trim() || "Your place";
  const c = city?.trim();
  return c ? `${n} in ${c}` : n;
}

function finishedLabel(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `Finished ${format(d, "EEE, MMM d")} at ${format(d, "h:mm a")}`;
}

function dayPartPhrase(iso: string | null): string {
  if (!iso) return "today";
  const h = new Date(iso).getHours();
  if (Number.isNaN(h)) return "today";
  if (h < 12) return "this morning";
  if (h < 17) return "this afternoon";
  return "this evening";
}

function issueTag(severity: string | null): { label: string; className: string } {
  const s = (severity || "").toLowerCase();
  if (s === "high" || s === "critical" || s === "major" || s === "urgent") {
    return { label: "WORTH KNOWING", className: "bg-[#fdf4e3] text-[#8a6218]" };
  }
  return { label: "MINOR", className: "bg-[#eef2f1] text-[#5c6b74]" };
}

function Shell({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "min-h-screen bg-[#f7f9f8] font-sans text-[#0f1720]",
        className,
      )}
    >
      <div className="mx-auto w-full max-w-[480px]">{children}</div>
    </div>
  );
}

function BrandMark({ light }: { light?: boolean }) {
  return (
    <div
      className={cn(
        "text-[11px] font-bold tracking-[1.4px]",
        light ? "text-white/90" : "text-[#15776d]",
      )}
    >
      STRAVO
    </div>
  );
}

function Hero({
  photoUrl,
  headline,
  support,
  skeleton,
}: {
  photoUrl?: string | null;
  headline: string;
  support?: string | null;
  skeleton?: boolean;
}) {
  return (
    <div className="relative h-[430px] w-full overflow-hidden bg-[#dfe8e4]">
      {skeleton ? (
        <div className="absolute inset-0 animate-pulse bg-gradient-to-b from-[#e4ebe8] to-[#d5dfdb]" />
      ) : photoUrl ? (
        <img src={photoUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-[repeating-linear-gradient(135deg,#e8efec_0_10px,#dbe5e1_10px_20px)]" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0f1720]/78] via-[#15776d]/45 to-[#24a89a]/25" />
      <div className="absolute inset-0 flex flex-col justify-end px-[26px] pb-8 pt-10">
        <BrandMark light />
        {skeleton ? (
          <div className="mt-5 space-y-3">
            <div className="h-9 w-4/5 rounded-md bg-white/25" />
            <div className="h-4 w-full rounded-md bg-white/15" />
            <div className="h-4 w-3/5 rounded-md bg-white/15" />
          </div>
        ) : (
          <>
            <h1 className="mt-4 text-[34px] font-bold leading-[1.1] tracking-[-0.9px] text-white">
              {headline}
            </h1>
            {support ? (
              <p className="mt-3 text-[15px] font-medium leading-relaxed text-white/85">{support}</p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[11px] font-bold tracking-[1px] text-[#8494a2]">{children}</div>
  );
}

const GuestReportPage = () => {
  const { token } = useParams<{ token: string }>();
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [checklistOpen, setChecklistOpen] = useState(false);

  const { data: report, isLoading, error } = useQuery({
    queryKey: ["guest-report", token],
    queryFn: () => fetchGuestReport(token!),
    enabled: !!token,
    retry: false,
  });

  const curatedPhotos = useMemo(() => {
    if (!report) return [] as { url: string; label: string }[];
    const fromProof = (report.proof_photos ?? []).filter(Boolean).map((url) => ({
      url,
      label: "Photo",
    }));
    const fromTasks = (report.photos ?? [])
      .filter((p) => p.photo_url)
      .map((p) => ({
        url: p.photo_url,
        label: p.room_name?.trim() || "Room",
      }));
    const seen = new Set<string>();
    const merged: { url: string; label: string }[] = [];
    for (const p of [...fromProof, ...fromTasks]) {
      if (seen.has(p.url)) continue;
      seen.add(p.url);
      merged.push(p);
      if (merged.length >= 5) break;
    }
    return merged;
  }, [report]);

  const heroPhoto = curatedPhotos[0]?.url ?? null;
  const lookAround = curatedPhotos.slice(1);

  if (!token) {
    return (
      <Shell>
        <Hero
          headline="This link has expired"
          support="Cleaning reports stay open for 30 days. Your host can send you a fresh link any time."
        />
      </Shell>
    );
  }

  if (isLoading) {
    return (
      <Shell>
        <Hero headline="" skeleton />
      </Shell>
    );
  }

  if (error || report == null) {
    return (
      <Shell>
        <Hero
          headline="This link has expired"
          support="Cleaning reports stay open for 30 days. Your host can send you a fresh link any time."
        />
        <p className="px-[26px] pb-[34px] pt-6 text-[13px] font-medium text-[#8494a2]">
          No property details are shown for an expired or invalid link.
        </p>
      </Shell>
    );
  }

  const place = placeLine(report.property_name, report.property_city);
  const finished = finishedLabel(report.report_created_at);
  const when = dayPartPhrase(report.report_created_at);
  const issues = report.issues ?? [];
  const hasIssues = issues.length > 0;

  // State E — cleaning not finished: honest, no partial checklist
  if (!report.cleaning_completed) {
    return (
      <Shell>
        <Hero
          photoUrl={heroPhoto}
          headline="Cleaning is still under way"
          support={`${place}. This page updates on its own once the clean is finished.`}
        />
        <div className="px-[26px] pb-[34px] pt-[26px]">
          <h2 className="text-[19px] font-bold tracking-[-0.3px] text-[#0f1720]">Not finished yet</h2>
          <p className="mt-2 text-[15px] font-medium leading-relaxed text-[#5c6b74]">
            We’ll show your photos and anything you should know as soon as the clean is signed off.
          </p>
          {finished ? (
            <p className="mt-3 text-[12.5px] font-medium text-[#8494a2]">
              Last updated {format(new Date(report.report_created_at!), "h:mm a")}
            </p>
          ) : null}
        </div>
      </Shell>
    );
  }

  const rooms: Room[] = Array.isArray(report.checklist_rooms) ? report.checklist_rooms : [];
  const validRooms = rooms
    .filter((r) => r?.name?.trim())
    .map((r) => ({
      ...r,
      tasks: (r.tasks ?? []).filter((t) => getTaskText(t as Task).trim()),
    }));
  const tasksStatus = report.tasks_status ?? [];
  const roomSummaries = validRooms.map((room) => {
    const tasks = room.tasks ?? [];
    const done = tasks.filter((t) =>
      tasksStatus.some(
        (s) =>
          s.room_name.trim() === room.name.trim() &&
          s.task_text.trim() === getTaskText(t as Task).trim() &&
          s.completed,
      ),
    ).length;
    const allDone = tasks.length === 0 || done === tasks.length;
    return {
      name: room.name,
      label: allDone ? "cleaned" : `${done} of ${tasks.length} checked`,
    };
  });

  const supportHappy = `${place} was cleaned and checked ${when}. Nothing needs your attention.`;
  const supportIssues = `${place} was cleaned ${when}. There ${
    issues.length === 1 ? "is one small thing" : `are ${issues.length} small things`
  } we want you to know about.`;

  const statusBodyHappy =
    "Every room was cleaned, restocked and looked over. No damage or problems were found.";
  const statusBodyIssues =
    issues.length === 1
      ? "Every room was cleaned and restocked. One thing won’t affect your stay, but your host already knows about it."
      : "Every room was cleaned and restocked. A few things won’t affect your stay, but your host already knows about them.";

  return (
    <Shell>
      <Hero
        photoUrl={heroPhoto}
        headline="Your place is ready"
        support={hasIssues ? supportIssues : supportHappy}
      />

      <div className="px-[26px] pb-[34px]">
        {/* Status */}
        <section className="border-b border-[#e4eae8] pt-[26px] pb-[26px]">
          <h2 className="text-[19px] font-bold tracking-[-0.3px] text-[#0f1720]">
            Cleaned and ready for you
          </h2>
          <p className="mt-2 text-[15px] font-medium leading-relaxed text-[#5c6b74]">
            {hasIssues ? statusBodyIssues : statusBodyHappy}
          </p>
          {finished ? (
            <p className="mt-3 text-[12.5px] font-medium text-[#8494a2]">{finished}</p>
          ) : null}
        </section>

        {/* Things to know */}
        {hasIssues ? (
          <section className="border-b border-[#e4eae8] pt-[26px] pb-[26px]">
            <SectionLabel>THINGS TO KNOW</SectionLabel>
            <div className="mt-3.5 flex flex-col gap-3.5">
              {issues.map((issue, idx) => {
                const tag = issueTag(issue.severity);
                const title = issue.description?.trim() || issue.room_name || "Note";
                const body =
                  issue.room_name && issue.description
                    ? `In the ${issue.room_name}. Your host has been notified.`
                    : "Your host has been notified.";
                return (
                  <div key={idx} className="flex gap-3.5">
                    {issue.photo_url ? (
                      <button
                        type="button"
                        onClick={() => setLightboxUrl(issue.photo_url!)}
                        className="h-[76px] w-[76px] shrink-0 overflow-hidden rounded-[12px] border border-[#e4eae8] bg-[#eef2f1]"
                      >
                        <img
                          src={issue.photo_url}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      </button>
                    ) : (
                      <div className="h-[76px] w-[76px] shrink-0 rounded-[12px] bg-[#eef2f1]" />
                    )}
                    <div className="min-w-0 flex-1">
                      <span
                        className={cn(
                          "inline-flex rounded-full px-2.5 py-0.5 text-[11.5px] font-bold tracking-[0.4px]",
                          tag.className,
                        )}
                      >
                        {tag.label}
                      </span>
                      <div className="mt-1.5 text-base font-bold leading-snug text-[#0f1720]">
                        {title}
                      </div>
                      <p className="mt-1 text-[15px] font-medium leading-relaxed text-[#5c6b74]">
                        {body}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ) : null}

        {/* A look around — featured + pair; hero photo already used above */}
        {lookAround.length > 0 ? (
          <section className="border-b border-[#e4eae8] pt-[26px] pb-[26px]">
            <SectionLabel>A LOOK AROUND</SectionLabel>
            <div className="mt-3.5 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => setLightboxUrl(lookAround[0].url)}
                className="relative h-[200px] w-full overflow-hidden rounded-2xl bg-[#eef2f1]"
              >
                <img src={lookAround[0].url} alt="" className="h-full w-full object-cover" />
                {lookAround[0].label && lookAround[0].label !== "Photo" ? (
                  <span className="absolute bottom-2.5 left-2.5 rounded-md bg-black/45 px-2 py-0.5 text-[11px] font-bold text-white">
                    {lookAround[0].label}
                  </span>
                ) : null}
              </button>
              {lookAround.length > 1 ? (
                <div className="flex gap-2">
                  {lookAround.slice(1, 3).map((p, i) => (
                    <button
                      key={`${p.url}-${i}`}
                      type="button"
                      onClick={() => setLightboxUrl(p.url)}
                      className="relative h-[132px] min-w-0 flex-1 overflow-hidden rounded-2xl bg-[#eef2f1]"
                    >
                      <img src={p.url} alt="" className="h-full w-full object-cover" />
                      {p.label && p.label !== "Photo" ? (
                        <span className="absolute bottom-2 left-2 rounded-md bg-black/45 px-2 py-0.5 text-[11px] font-bold text-white">
                          {p.label}
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <p className="mt-3 text-[12.5px] font-medium text-[#8494a2]">
              Tap any photo to see it full size
            </p>
          </section>
        ) : null}

        {/* Collapsed checklist — only tappable bordered row */}
        {roomSummaries.length > 0 ? (
          <section className="pt-[26px]">
            <button
              type="button"
              onClick={() => setChecklistOpen((o) => !o)}
              className="flex w-full items-center gap-3 rounded-[14px] border-[1.5px] border-[#e4eae8] bg-white px-4 py-3.5 text-left"
            >
              <div className="min-w-0 flex-1">
                <div className="text-base font-bold text-[#0f1720]">What we cleaned</div>
                <div className="mt-0.5 text-[13px] font-medium text-[#8494a2]">
                  {roomSummaries.length} room{roomSummaries.length === 1 ? "" : "s"}
                  {report.all_tasks_completed ? " · everything on the list" : ""}
                </div>
              </div>
              {checklistOpen ? (
                <ChevronUp className="h-5 w-5 shrink-0 text-[#8494a2]" />
              ) : (
                <ChevronDown className="h-5 w-5 shrink-0 text-[#8494a2]" />
              )}
            </button>
            {checklistOpen ? (
              <div className="mt-3 flex flex-col gap-2.5 px-1">
                {roomSummaries.map((r) => (
                  <div key={r.name} className="flex items-baseline justify-between gap-3">
                    <span className="text-[15px] font-bold text-[#0f1720]">{r.name}</span>
                    <span className="shrink-0 text-[13px] font-medium text-[#8494a2]">{r.label}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </section>
        ) : null}

        <p className="mt-[34px] text-center text-[12.5px] font-medium leading-relaxed text-[#8494a2]">
          Cleaning verified by Stravo · this link is private to your stay
        </p>
      </div>

      {lightboxUrl ? (
        <button
          type="button"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setLightboxUrl(null)}
          aria-label="Close photo"
        >
          <img
            src={lightboxUrl}
            alt=""
            className="max-h-full max-w-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </button>
      ) : null}
    </Shell>
  );
};

export default GuestReportPage;
